import { MouseEvent, ReactElement } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Heading, Link, Paragraph, Tag } from '@digdir/designsystemet-react';
import type { RiskMatrixOverview, RiskMatrixVersion } from '../../../../api/hms';
import { Routes } from '../../../../enums/Routes';
import { approvedBy, formatDay } from '../../hmsFormat';
import type { Action } from '../../hmsActions';
import { PendingActions } from '../../components/PendingActions';
import { ReturnedAlert } from '../../components/ReturnedAlert';
import layout from '../../hmsPage.module.css';
import classes from '../HRRiskMatrix.module.css';

type Props = {
  overview: RiskMatrixOverview;
  sendSectionId: string;
  unsentChanges: number;
  justApproved: boolean;
  approve: Action<[id: number, comment: string]>;
  withdraw: Action<[id: number]>;
  actionFailed: boolean;
};

export const SidePanel = ({
  overview,
  sendSectionId,
  unsentChanges,
  justApproved,
  approve,
  withdraw,
  actionFailed,
}: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  const { current, pending, returned } = overview;
  const day = (version: RiskMatrixVersion) =>
    formatDay(version.approval?.approvedAt ?? version.createdAt, t, i18n.language);
  const customFactors = overview.factors.filter((factor) => factor.source === 'CUSTOM').length;

  const goToSend = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    const section = document.getElementById(sendSectionId);
    if (!section) return;
    window.scrollTo({ top: section.getBoundingClientRect().top + window.scrollY - 24, behavior: 'smooth' });
    section.querySelector<HTMLElement>('textarea')?.focus({ preventScroll: true });
  };

  return (
    <div className={layout.side}>
      <section aria-labelledby="hr-rm-status" className={layout.sideCard}>
        <div className={classes.statusHeading}>
          <Heading level={2} data-size="xs" id="hr-rm-status">
            {t('hrRiskMatrix.status.title', { version: current.version })}
          </Heading>
          <Tag data-size="sm" data-color="success">
            {t('hrRiskMatrix.status.approved')}
          </Tag>
        </div>
        <dl className={classes.facts}>
          <div>
            <dt className="ds-paragraph" data-size="sm">{t('hrRiskMatrix.status.approvedLabel')}</dt>
            <dd className="ds-paragraph" data-size="sm">
              {current.approval
                ? t('hrRiskMatrix.status.approvedValue', { date: day(current), by: approvedBy(current.approval, t) })
                : t('hrRiskMatrix.status.standardValue', { date: day(current) })}
            </dd>
          </div>
          <div>
            <dt className="ds-paragraph" data-size="sm">{t('hrRiskMatrix.status.appliesLabel')}</dt>
            <dd className="ds-paragraph" data-size="sm">
              {t('hrRiskMatrix.status.appliesValue', {
                count: overview.teamCount,
                organization: overview.organizationName,
              })}
            </dd>
          </div>
        </dl>
        {pending && (
          <Alert data-color="info" className={classes.pendingAlert}>
            <Paragraph data-size="sm">
              {t('hrRiskMatrix.status.pending', {
                version: pending.version,
                date: formatDay(pending.submittedAt ?? pending.createdAt, t, i18n.language),
              })}
            </Paragraph>
            <PendingActions
              id="hr-rm-pending-comment"
              ownVersion={overview.pendingIsYours}
              approve={{ run: (comment) => approve.run(pending.id, comment), pending: approve.pending }}
              withdraw={{ run: () => withdraw.run(pending.id), pending: withdraw.pending }}
            />
          </Alert>
        )}
        {returned?.returned && <ReturnedAlert version={returned.version} returned={returned.returned} />}
        {actionFailed && (
          <Alert data-color="danger">
            <Paragraph data-size="sm">{t('hms.errors.save')}</Paragraph>
          </Alert>
        )}
        {unsentChanges > 0 && (
          <Alert data-color="warning">
            <Paragraph data-size="sm">
              {t('hrRiskMatrix.status.unsent', { count: unsentChanges })}{' '}
              <Link href={`#${sendSectionId}`} onClick={goToSend}>
                {t('hrRiskMatrix.status.goToSend')}
              </Link>
            </Paragraph>
          </Alert>
        )}
        {justApproved && (
          <Alert data-color="success">
            <Paragraph data-size="sm">{t('hrRiskMatrix.status.justApproved', { version: current.version })}</Paragraph>
          </Alert>
        )}
      </section>

      <section aria-labelledby="hr-rm-factors" className={layout.sideCard} data-gap="sm">
        <Heading level={2} data-size="xs" id="hr-rm-factors">
          {t('hrRiskMatrix.factors.title')}
        </Heading>
        <Paragraph data-size="sm" className={layout.subtle}>
          {t('hrRiskMatrix.factors.summary', { count: overview.factors.length, custom: customFactors })}
        </Paragraph>
        <Button asChild variant="secondary" data-size="sm" className={layout.startAligned}>
          <RouterLink to={Routes.HRRiskFactorsFullPath}>{t('hrRiskMatrix.factors.manage')}</RouterLink>
        </Button>
      </section>

      <section aria-labelledby="hr-rm-history" className={layout.sideCard} data-gap="sm">
        <Heading level={2} data-size="xs" id="hr-rm-history">
          {t('hrRiskMatrix.history.title')}
        </Heading>
        <ol className={classes.history}>
          {overview.history.map((version) => (
            <li key={version.id}>
              <span className="ds-label" data-size="sm">
                {t('hrRiskMatrix.history.version', { version: version.version, date: day(version) })}
              </span>
              <span className="ds-paragraph" data-size="sm">
                {version.changeReason ?? t('hrRiskMatrix.history.firstVersion')}
              </span>
              {version.approval && (
                <span className={`ds-paragraph ${layout.subtle}`} data-size="xs">
                  {t('hrRiskMatrix.history.approvedBy', { by: approvedBy(version.approval, t) })}
                </span>
              )}
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
};
